.wrapper {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.panel {
  background: var(--surface);
  border-radius: var(--radius);
  padding: 24px;
  min-width: 0;
}

.heading {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 4px 10px;
  margin-bottom: 14px;
  /* Both halves are nowrap, so on very narrow phones the note has to drop to
     its own line instead of pushing the panel past the viewport. */
  flex-wrap: wrap;
}

.label {
  font-family: var(--font-display);
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--text);
  white-space: nowrap;
}

/* Athlete tag on the right of a top-exercises heading, carrying that athlete's
   accent without uppercasing the username. */
.owner {
  font-size: 0.85rem;
  font-weight: 600;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--accent);
}

.note {
  font-size: 0.82rem;
  color: var(--muted);
  white-space: nowrap;
}

/* Common exercises section */
/* aStats/bStats share the leftover space (like the Key metrics row) so the
   comparison hugs the middle instead of sitting pinned to the right edge
   with a big gap after the exercise name on wide panels. */
.commonHeader {
  display: grid;
  grid-template-columns: minmax(0, 240px) minmax(0, 1fr) 80px minmax(0, 1fr);
  gap: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--border);
  margin-bottom: 4px;
}

.colA,
.colB {
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--accent);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

.colA {
  text-align: right;
}

.commonList {
  display: flex;
  flex-direction: column;
}

.commonRow {
  display: grid;
  grid-template-columns: minmax(0, 240px) minmax(0, 1fr) 80px minmax(0, 1fr);
  align-items: center;
  gap: 8px;
  padding: 10px 0;
  border-bottom: 1px solid var(--border);
  animation: rowIn 0.35s ease both;
  animation-delay: calc(var(--i, 0) * 30ms);
}

.commonRow:last-child {
  border-bottom: none;
}

/* Three or four athletes: one self-labelled column each, then the lead badge.
   The middle badge of the two-athlete row only works because there is a middle. */
.commonRow.many {
  grid-template-columns: minmax(0, 200px) repeat(var(--n, 2), minmax(0, 1fr)) auto;
}

.exName {
  font-size: 0.92rem;
  font-weight: 600;
  color: var(--text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.aStats {
  display: flex;
  flex-direction: column;
  gap: 1px;
  align-items: flex-end;
  text-align: right;
}

.bStats {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.statName {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--accent);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}

.statVal {
  font-size: 0.92rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: var(--text);
}

.statSub {
  font-size: 0.8rem;
  color: var(--muted);
}

/* Lead badge */
.lead {
  font-size: 0.8rem;
  font-weight: 700;
  text-align: center;
  padding: 2px 8px;
  border-radius: 999px;
  white-space: nowrap;
}

.leadOn {
  color: var(--accent);
  background: var(--accent-dim);
}

.lead.flat {
  color: var(--muted);
}

/* Top exercises — shared heading + sort toggle above the two columns */
.topSection {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.topSectionHead {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.toggle {
  display: flex;
  gap: 2px;
  padding: 2px;
  background: var(--surface-2);
  border-radius: 6px;
}

.toggleBtn {
  padding: 4px 10px;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--soft);
  border-radius: 5px;
  transition: background 0.15s, color 0.15s;
}

.toggleBtn:hover {
  color: var(--text);
}

.toggleActive {
  background: var(--canvas);
  color: var(--text);
  box-shadow: 0 1px 3px rgba(38, 33, 25, 0.12);
}

.topRow {
  display: grid;
  grid-template-columns: repeat(var(--n, 2), minmax(0, 1fr));
  gap: 16px;
}

.topList {
  display: flex;
  flex-direction: column;
  gap: 0;
}

@keyframes rowIn {
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
}

.topItem {
  display: grid;
  grid-template-columns: 20px minmax(0, 1fr) auto;
  align-items: center;
  gap: 8px;
  padding: 8px 6px;
  border-bottom: 1px solid var(--border);
  animation: rowIn 0.35s ease both;
  animation-delay: calc(var(--i, 0) * 35ms);
}

.topItem:last-child {
  border-bottom: none;
}

/* Exercises appearing in more than one top list — a faint tint plus a marker
   dot so the same movement can be paired across the columns. */
.shared {
  background: var(--tint);
  border-radius: 6px;
}

.sharedDot {
  display: inline-block;
  width: 6px;
  height: 6px;
  margin-right: 7px;
  border-radius: 50%;
  background: var(--soft);
  vertical-align: middle;
}

.rank {
  font-size: 0.82rem;
  font-variant-numeric: tabular-nums;
  color: var(--muted);
  text-align: center;
}

.exNameTop {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.topStatBlock {
  display: flex;
  flex-direction: column;
  gap: 1px;
  align-items: flex-end;
  text-align: right;
}

.topStatVal {
  font-size: 0.88rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: var(--soft);
  white-space: nowrap;
}

.topStatSub {
  font-size: 0.8rem;
  color: var(--muted);
  white-space: nowrap;
}

/* Four self-labelled columns need more width than a laptop has next to the
   exercise name, so they drop to two before dropping to one. */
@media (max-width: 1100px) {
  .topRow {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .commonRow.many {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .commonRow.many .exName {
    grid-column: 1 / -1;
  }

  .commonRow.many .lead {
    justify-self: start;
    align-self: center;
  }
}

@media (max-width: 700px) {
  .topRow {
    grid-template-columns: minmax(0, 1fr);
  }

  /* Exercise name goes full-width on its own line; the A/lead/B comparison
     moves to a second line beneath it, instead of squeezing a long name
     and two stat blocks into one narrow row. */
  .commonRow {
    grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
    grid-template-areas:
      "name name name"
      "a lead b";
    row-gap: 6px;
    column-gap: 8px;
  }

  .exName {
    grid-area: name;
    white-space: normal;
    overflow: visible;
    text-overflow: clip;
    overflow-wrap: break-word;
  }

  .aStats {
    grid-area: a;
  }

  .lead {
    grid-area: lead;
  }

  .bStats {
    grid-area: b;
  }

  /* A three- or four-athlete row has no A/lead/B geometry to fall back to:
     the name keeps its own line and the columns pair up under it. */
  .commonRow.many {
    grid-template-areas: none;
  }

  .commonRow.many .exName {
    grid-area: auto;
    grid-column: 1 / -1;
  }

  .commonRow.many .aStats,
  .commonRow.many .lead {
    grid-area: auto;
  }

  .commonHeader {
    grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  }

  /* First spacer held the name column's place above the (now separate,
     full-width) name row — no longer needed once A sits in column 1. */
  .commonHeader > span:first-child {
    display: none;
  }
}

@media (max-width: 500px) {
  .statVal {
    font-size: 0.85rem;
  }
}
